// SPDX-License-Identifier: MIT
// Short, subdomain-safe handle derived from a project title. The provisioner
// uses it as the subdomain (<handle>.genesis-platform.fr); keep it short and
// human. This is only a PREFILL — the user can override in the wizard.

const STOP = new Set([
  'de', 'la', 'le', 'les', 'des', 'du', 'un', 'une', 'et', 'aux', 'au', 'en',
  'a', 'pour', 'd', 'l', 'the', 'of', 'for', 'to', 'and', 'with',
]);

function slugifyTokens(title: string): string[] {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-')
    .filter(Boolean);
}

export function shortHandle(title: string): string {
  const all = slugifyTokens(title);
  if (all.length === 0) return 'projet';
  const meaningful = all.filter((t) => !STOP.has(t));
  const source = meaningful.length ? meaningful : all;
  const kept: string[] = [];
  let len = 0;
  for (const tok of source) {
    const add = (kept.length ? 1 : 0) + tok.length;
    if (kept.length >= 1 && len + add > 24) break;
    kept.push(tok);
    len += add;
    if (kept.length === 2) break;
  }
  return kept.join('-').slice(0, 30);
}

/**
 * Validates a handle for use as a subdomain segment (<handle>.genesis-platform.fr).
 * Rules: 3–30 chars, lowercase alphanumeric segments separated by single hyphens,
 * no leading/trailing/double hyphens, no underscores or uppercase.
 * Returns { ok: true } on success or { ok: false, reason } on failure.
 */
export function validateHandle(h: string): { ok: boolean; reason?: string } {
  if (h.length < 3 || h.length > 30) return { ok: false, reason: 'length' };
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(h)) return { ok: false, reason: 'charset' };
  return { ok: true };
}

/**
 * Derives the subdomain label of an existing live deploy URL
 * (`https://<handle>.genesis-platform.fr` → `<handle>`). The redeploy path uses
 * it so a redeploy always lands on the SAME subdomain — the deploy-frontend
 * edge fn's fallback is slugify(title), which may differ when the user
 * customized the handle in the wizard. Returns null when the URL is absent,
 * unparseable, or its label would not survive validateHandle (caller then
 * falls back to shortHandle(title)).
 */
export function handleFromDeployUrl(deployUrl: string | null | undefined): string | null {
  if (!deployUrl) return null;
  try {
    const label = new URL(deployUrl).hostname.split('.')[0] ?? '';
    return validateHandle(label).ok ? label : null;
  } catch {
    return null;
  }
}
